import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { fetchStreams, getDefaultHttpBase } from './api/streamService';
import { PlayerControls } from './components/PlayerControls';
import { StreamList } from './components/StreamList';
import { ApiLogPanel } from './components/ApiLogPanel';
import { OpusPlayer } from './services/OpusPlayer';
import { PCMPlayer } from './services/PCMPlayer';
import { TextPlayer } from './services/TextPlayer';
import { StreamingSubscriber } from './services/StreamingSubscriber';
import { FormatSelector } from './components/FormatSelector';
import { TextDisplay } from './components/TextDisplay';
import {
  ConnectionState,
  DiagnosticsSnapshot,
  ApiLogEntry,
  StreamInfo,
  StreamMetadata,
  CapabilitiesResponse,
  TextFrame,
} from './types';

const getDefaultWebSocketBase = (): string => {
  if (typeof window === 'undefined') {
    return 'ws://localhost:5122';
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // Check if we are running under a subpath (e.g. /streaming/)
  const pathPrefix = window.location.pathname.includes('/streaming/') ? '/streaming' : '';
  return `${protocol}//${window.location.host}${pathPrefix}`;
};

const normalizeUrl = (value: string, fallback: string): string => {
  if (!value) return fallback;
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const getSubscribeUrl = (base: string, streamId: string, format: string = 'opus'): string => {
  const normalized =
    base.startsWith('ws://') || base.startsWith('wss://')
      ? normalizeUrl(base, base)
      : base.startsWith('http')
      ? normalizeUrl(
          base.replace(/^http/i, window?.location.protocol === 'https:' ? 'wss' : 'ws'),
          base,
        )
      : `ws://${normalizeUrl(base, base)}`;
  return `${normalized}/api/stream/subscribe/${streamId}?format=${format}`;
};

const isTextFormat = (formatId: string): boolean => {
  return formatId.startsWith('text-') || formatId.includes('stt');
};

const createDiagnostics = (): DiagnosticsSnapshot => ({
  packets: 0,
  audioFrames: 0,
  bytes: 0,
  lastSequence: 0,
  startedAt: Date.now(),
  lastUpdated: Date.now(),
  reconnects: 0,
});

const formatJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

function App() {
  const [streams, setStreams] = useState<StreamInfo[]>([]);
  const [streamsLoading, setStreamsLoading] = useState(false);
  const [lastStreamRefresh, setLastStreamRefresh] = useState<number | null>(null);
  const [serverHttpBase, setServerHttpBase] = useState(getDefaultHttpBase());
  const [serverWsBase, setServerWsBase] = useState(getDefaultWebSocketBase());
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [metadata, setMetadata] = useState<StreamMetadata | null>(null);
  const [diagnostics, setDiagnostics] = useState<DiagnosticsSnapshot>(createDiagnostics);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [apiLogs, setApiLogs] = useState<ApiLogEntry[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<string>('opus');
  const [capabilities, setCapabilities] = useState<CapabilitiesResponse | null>(null);
  const [textHistory, setTextHistory] = useState<TextFrame[]>([]);
  const [latestText, setLatestText] = useState<string | null>(null);

  const subscriberRef = useRef<StreamingSubscriber | null>(null);
  const opusPlayerRef = useRef<OpusPlayer | null>(null);
  const pcmPlayerRef = useRef<PCMPlayer | null>(null);
  const textPlayerRef = useRef<TextPlayer | null>(null);
  const pauseRef = useRef(false);
  const metadataInitializedRef = useRef(false);
  const logCounterRef = useRef(0);


  const appendLog = useCallback(
    (entry: Omit<ApiLogEntry, 'id' | 'timestamp'> & { timestamp?: number }) => {
      logCounterRef.current += 1;
      const newEntry: ApiLogEntry = {
        id: `api-log-${logCounterRef.current}`,
        timestamp: entry.timestamp ?? Date.now(),
        title: entry.title,
        message: entry.message,
        level: entry.level,
      };
      setApiLogs((prev) => [newEntry, ...prev].slice(0, 120));
    },
    [],
  );

  const clearLogs = useCallback(() => {
    setApiLogs([]);
  }, []);

  const refreshStreams = useCallback(async () => {
    setStreamsLoading(true);
    const listUrl = `${serverHttpBase.replace(/\/$/, '')}/api/debug/streams`;
    appendLog({
      level: 'info',
      title: 'Request',
      message: `GET ${listUrl}\nbody: (none)`,
    });
    try {
      const data = await fetchStreams(serverHttpBase);
      setStreams(data);
      if (data.length > 0 && !selectedStreamId) {
        setSelectedStreamId(data[0].streamId);
      }
      setLastStreamRefresh(Date.now());
      appendLog({
        level: 'success',
        title: 'Response',
        message: `GET ${listUrl} -> ${data.length} streams\nresponse:\n${formatJson(data)}`,
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : String(error));
      appendLog({
        level: 'error',
        title: 'Failed',
        message: `GET ${listUrl}\nError: ${(error as Error).message ?? error}`,
      });
    } finally {
      setStreamsLoading(false);
    }
  }, [serverHttpBase, selectedStreamId, appendLog]);

  useEffect(() => {
    refreshStreams();
  }, [refreshStreams]);

  const updateDiagnostics = () => {
    const subscriber = subscriberRef.current;
    if (subscriber) {
      setDiagnostics(subscriber.getDiagnostics());
    }
  };

  const attachSubscriber = (streamId: string) => {
    const subscriber = new StreamingSubscriber({
      serverUrl: getSubscribeUrl(serverWsBase, streamId, selectedFormat),
      onAudioFrame: async (frame) => {
        if (pauseRef.current) {
          return;
        }
        // Only play if we have metadata (player initialized)
        if (!metadataInitializedRef.current) {
          return;
        }
        try {
          if (isTextFormat(selectedFormat)) {
            await textPlayerRef.current?.playTextFrame(frame);
          } else if (selectedFormat === 'pcm') {
            await pcmPlayerRef.current?.playPCMFrame(frame);
          } else {
            await opusPlayerRef.current?.playOpusFrame(frame);
          }
          updateDiagnostics();
        } catch (error) {
          // Errors are already handled in players, just update diagnostics
          updateDiagnostics();
        }
      },
      onMetadata: async (meta, rawPayload) => {
        appendLog({
          level: 'info',
          title: 'WebSocket Message',
          message: `Received metadata (raw):\n${rawPayload}`,
        });
        setMetadata(meta);
        try {
          if (isTextFormat(selectedFormat)) {
            if (!textPlayerRef.current) {
              textPlayerRef.current = new TextPlayer({
                onTextReceived: (textFrame) => {
                  setTextHistory(prev => [...prev, textFrame].slice(-100)); // Keep last 100
                  setLatestText(textFrame.text);
                  appendLog({
                    level: 'success',
                    title: 'Text Received',
                    message: `"${textFrame.text}" (confidence: ${(textFrame.confidence * 100).toFixed(1)}%)`,
                  });
                },
                onError: (error) => {
                  appendLog({
                    level: 'error',
                    title: 'Text Player Error',
                    message: error.message,
                  });
                }
              });
            } else {
              await textPlayerRef.current.reset();
            }
            await textPlayerRef.current.initialize();
          } else if (selectedFormat === 'pcm') {
            if (!pcmPlayerRef.current) {
              pcmPlayerRef.current = new PCMPlayer();
            } else {
              await pcmPlayerRef.current.reset();
            }
            await pcmPlayerRef.current.initialize({
              sampleRate: meta.sampleRate,
              channels: meta.channels,
              bitDepth: meta.bitDepth,
            });
            pcmPlayerRef.current.setVolume(volume);
            pcmPlayerRef.current.setMuted(muted);
          } else {
            if (!opusPlayerRef.current) {
              opusPlayerRef.current = new OpusPlayer();
            } else {
              await opusPlayerRef.current.reset();
            }
            await opusPlayerRef.current.initialize({
            sampleRate: meta.sampleRate,
            channels: meta.channels,
          });
            opusPlayerRef.current.setVolume(volume);
            opusPlayerRef.current.setMuted(muted);
          }
          metadataInitializedRef.current = true;
          appendLog({
            level: 'success',
            title: 'Subscription Success',
            message: `Parsed metadata:\n${formatJson(meta)}\nFormat: ${selectedFormat.toUpperCase()}`,
          });
        } catch (error) {
          metadataInitializedRef.current = false;
          setErrorMessage(
            `Failed to initialize player: ${error instanceof Error ? error.message : String(error)}`,
          );
          appendLog({
            level: 'error',
            title: 'Player Initialization Failed',
            message: error instanceof Error ? error.message : String(error),
          });
        }
      },
      onControl: (data) => {
        if (data.type === 3) {
          setErrorMessage('Server notification: Stream has ended');
          handleDisconnect();
          appendLog({
            level: 'info',
            title: 'Control Message',
            message: `Stream ended:\n${formatJson(data)}`,
          });
        } else {
          appendLog({
            level: 'info',
            title: 'Control Message',
            message: `Type ${data.type ?? 'unknown'}:\n${formatJson(data)}`,
          });
        }
      },
      onError: (err) => {
        setDiagnostics((prev) => ({ ...prev, lastError: err.message, lastUpdated: Date.now() }));
        setErrorMessage(err.message);
        appendLog({
          level: 'error',
          title: 'WebSocket Error',
          message: err.message,
        });
      },
      onConnectionChange: (state) => {
        switch (state) {
          case 'connected':
            setConnectionState('connected');
            setErrorMessage(null);
            appendLog({
              level: 'success',
              title: 'WebSocket',
              message: `Connected ${getSubscribeUrl(serverWsBase, streamId, selectedFormat)}`,
            });
            break;
          case 'connecting':
            setConnectionState('connecting');
            appendLog({
              level: 'info',
              title: 'WebSocket',
              message: `Connecting ${getSubscribeUrl(serverWsBase, streamId)}`,
            });
            break;
          case 'error':
            setConnectionState('error');
            appendLog({
              level: 'error',
              title: 'WebSocket',
              message: `Connection error ${getSubscribeUrl(serverWsBase, streamId)}`,
            });
            break;
          case 'closed':
            setConnectionState('idle');
            appendLog({
              level: 'info',
              title: 'WebSocket',
              message: `Connection closed ${getSubscribeUrl(serverWsBase, streamId)}`,
            });
            break;
          default:
            break;
        }
      },
      onClose: () => {
        setConnectionState('idle');
        appendLog({
          level: 'info',
          title: 'WebSocket',
          message: 'Server closed the connection',
        });
      },
    });

    subscriberRef.current = subscriber;
    return subscriber;
  };

  const handleConnect = async () => {
    if (!selectedStreamId) {
      setErrorMessage('Please select a stream first');
      return;
    }

    appendLog({
      level: 'info',
      title: 'Subscribe',
      message: `Preparing to connect WebSocket:\nURL: ${getSubscribeUrl(serverWsBase, selectedStreamId, selectedFormat)}\nStreamId: ${selectedStreamId}\nFormat: ${selectedFormat.toUpperCase()}`,
    });

    handleDisconnect();
    setConnectionState('connecting');
    setDiagnostics(createDiagnostics());
    pauseRef.current = false;
    setIsPaused(false);

    const subscriber = attachSubscriber(selectedStreamId);

    try {
      await subscriber.connect();
    } catch (error) {
      setConnectionState('error');
      setErrorMessage(error instanceof Error ? error.message : String(error));
      appendLog({
        level: 'error',
        title: 'WebSocket',
        message: `Connection error: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  };

  const handleDisconnect = () => {
    subscriberRef.current?.cleanup();
    subscriberRef.current = null;
    opusPlayerRef.current?.dispose();
    opusPlayerRef.current = null;
    pcmPlayerRef.current?.dispose();
    pcmPlayerRef.current = null;
    textPlayerRef.current?.dispose();
    textPlayerRef.current = null;
    pauseRef.current = false;
    setIsPaused(false);
    setConnectionState('idle');
    setMetadata(null);
    setTextHistory([]);
    setLatestText(null);
    metadataInitializedRef.current = false;
    appendLog({
      level: 'info',
      title: 'Subscribe',
      message: 'Disconnected from current stream subscription',
    });
  };

  const handlePause = async () => {
    pauseRef.current = true;
    setIsPaused(true);
    if (isTextFormat(selectedFormat)) {
      await textPlayerRef.current?.suspend();
    } else if (selectedFormat === 'pcm') {
      await pcmPlayerRef.current?.suspend();
    } else {
      await opusPlayerRef.current?.suspend();
    }
  };

  const handleResume = async () => {
    pauseRef.current = false;
    setIsPaused(false);
    if (isTextFormat(selectedFormat)) {
      await textPlayerRef.current?.resume();
    } else if (selectedFormat === 'pcm') {
      await pcmPlayerRef.current?.resume();
    } else {
      await opusPlayerRef.current?.resume();
    }
  };

  const handleVolumeChange = (value: number) => {
    setVolume(value);
    if (!isTextFormat(selectedFormat)) {
      if (selectedFormat === 'pcm') {
        pcmPlayerRef.current?.setVolume(value);
      } else {
        opusPlayerRef.current?.setVolume(value);
      }
    }
  };

  const handleToggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      if (!isTextFormat(selectedFormat)) {
        if (selectedFormat === 'pcm') {
          pcmPlayerRef.current?.setMuted(next);
        } else {
          opusPlayerRef.current?.setMuted(next);
        }
      }
      return next;
    });
  };

  const handleFormatChange = (formatId: string) => {
    // Disconnect if currently connected to avoid format mismatch
    if (connectionState === 'connected') {
      handleDisconnect();
    }
    setSelectedFormat(formatId);
    setTextHistory([]);
    setLatestText(null);
  };

  const handleCapabilitiesLoad = (caps: CapabilitiesResponse) => {
    setCapabilities(caps);
    appendLog({
      level: 'success',
      title: 'Capabilities Loaded',
      message: `Server supports ${caps.count} formats:\n${caps.formats.map(f => f.formatId).join(', ')}`,
    });
  };


  return (
    <div className="app-shell">
      <header className="app-header">
        <div>
          <h1>Streaming Subscribe Demo</h1>
          <p>React + TypeScript example for debugging server-side audio downlink</p>
        </div>
        <div className="server-config">
          <label>
            HTTP Base
            <input
              type="text"
              value={serverHttpBase}
              onChange={(e) => setServerHttpBase(e.target.value)}
            />
          </label>
          <label>
            WS Base
            <input
              type="text"
              value={serverWsBase}
              onChange={(e) => setServerWsBase(e.target.value)}
            />
          </label>
        </div>
      </header>

      {errorMessage && <div className="banner banner-error">{errorMessage}</div>}

      <main className="layout">
        <StreamList
          streams={streams}
          selectedStreamId={selectedStreamId}
          onSelect={setSelectedStreamId}
          loading={streamsLoading}
          onRefresh={refreshStreams}
          lastUpdated={lastStreamRefresh}
        />

        <div className="right-column">
          <FormatSelector
            serverHttpBase={serverHttpBase}
            selectedFormat={selectedFormat}
            onFormatChange={handleFormatChange}
            onCapabilitiesLoad={handleCapabilitiesLoad}
            onError={setErrorMessage}
          />

          <PlayerControls
            connectionState={connectionState}
            metadata={metadata}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onPause={handlePause}
            onResume={handleResume}
            canControl={connectionState === 'connected'}
            isPaused={isPaused}
            volume={volume}
            muted={muted}
            onVolumeChange={handleVolumeChange}
            onToggleMute={handleToggleMute}
            selectedStreamId={selectedStreamId}
            audioFormat={selectedFormat}
            onAudioFormatChange={handleFormatChange}
            isTextFormat={isTextFormat(selectedFormat)}
          />

          {isTextFormat(selectedFormat) && (
            <TextDisplay
              textHistory={textHistory}
              latestText={latestText}
              isActive={connectionState === 'connected'}
            />
          )}

          <ApiLogPanel logs={apiLogs} onClear={clearLogs} />
        </div>

        <div className="reference-column">
          <div className="reference-card">
            <h3>Protocol and Flow Documentation</h3>
            <p>
              Subscribe Demo is based on a custom binary streaming protocol. Each packet consists of an 8-byte header + payload, transmitted via WebSocket.
              The following details all data formats and operation flows.
            </p>

            <h4>1. REST API: Get Stream List</h4>
            <pre>
{`Request: GET /api/debug/streams

Response example:
[
  {
    "streamId": "stream_268aa1e5058b4f70ade5f5c02b84af06",
    "status": "Active",
    "createdAt": "2025-11-19T14:10:01.7605992Z",
    "subscriberCount": 0,
    "bytesReceived": 19141,
    "framesReceived": 65
  }
]`}
            </pre>

            <h4>2. WebSocket Packet Header Structure (8 bytes)</h4>
            <pre>
{`Offset | Size | Field     | Description
-------|------|-----------|------------------
0      | 2    | Magic     | Fixed value 0xCAFE (Little-Endian)
2      | 1    | Version   | Protocol version, currently 0x01
3      | 1    | Type      | Packet type (see table below)
4      | 2    | Sequence  | Sequence number (Little-Endian, starts from 0 and increments)
6      | 2    | Length    | Payload length (Little-Endian, unit: bytes)

Example (Metadata packet):
FE CA 01 02 26 00 4E 00
│  │  │  │  │  │  │  └─ Length: 0x004E = 78 bytes
│  │  │  │  └─┴──┘ Sequence: 0x0026 = 38
│  │  │  └─ Type: 0x02 = Metadata
│  │  └─ Version: 0x01
└─┴─ Magic: 0xCAFE`}
            </pre>

            <h4>3. Packet Type (PacketType) Enumeration</h4>
            <pre>
{`Value | Name       | Description
------|------------|------------------
0x00  | Audio      | Audio data packet, payload is Opus-encoded audio frame (binary)
0x01  | Control    | Control message packet, payload is JSON (see format below)
0x02  | Metadata   | Metadata packet, payload is StreamStart JSON (see format below)
0x03  | Heartbeat  | Heartbeat packet, payload is empty
0x04  | Ack        | Acknowledgment packet, payload is empty
0x05  | Error      | Error packet, payload is error message JSON`}
            </pre>

            <h4>4. Metadata Packet (Type=0x02) Format</h4>
            <p className="muted-text">After connection is established, the server first sends this packet, containing the stream's audio format information.</p>
            <pre>
{`Complete binary packet example:
[Header 8 bytes] + [Payload JSON]

Header: FE CA 01 02 00 00 4E 00
Payload (JSON string, UTF-8 encoded):
{
  "sampleRate": 16000,
  "channels": 1,
  "bitDepth": 16,
  "codec": "opus",
  "bitrate": 48000,
  "callInfo": {
    "phoneNumber": "+1234567890",
    "direction": "incoming",
    "timestamp": "2025-11-19T14:10:01.7605992Z"
  }
}`}
            </pre>

            <h4>5. Audio Packet (Type=0x00) Format</h4>
            <p className="muted-text">The server continuously sends this packet, each packet's payload is an Opus-encoded audio frame (binary data).</p>
            <pre>
{`Complete binary packet example:
[Header 8 bytes] + [Opus frame binary data]

Header: FE CA 01 00 27 00 80 00
        │  │  │  │  │  │  └─┴─ Length: 0x0080 = 128 bytes (Opus frame size)
        │  │  │  └─┴──┘ Sequence: 0x0027 = 39
        │  │  └─ Type: 0x00 = Audio
        │  └─ Version: 0x01
        └─┴─ Magic: 0xCAFE

Payload: [128 bytes of Opus-encoded audio frame, binary data]`}
            </pre>

            <h4>6. Control Packet (Type=0x01) Format</h4>
            <p className="muted-text">The server sends control messages, such as stream end notifications.</p>
            <pre>
{`Payload JSON format:
{
  "type": 3,  // 3 = StreamEnd (stream ended)
  "data": {
    "reason": "normal"  // or other reasons
  }
}

Other Control types:
- type: 1 = StreamStart (deprecated, use Metadata instead)
- type: 2 = StreamPause
- type: 3 = StreamEnd`}
            </pre>

            <h4>7. Complete Subscription Flow</h4>
            <ol>
              <li>
                <strong>Get Stream List</strong>
                <br />
                <code>GET /api/debug/streams</code> → Returns active stream list, get <code>streamId</code>
              </li>
              <li>
                <strong>Establish WebSocket Connection</strong>
                <br />
                <code>ws://host:port/api/stream/subscribe/{'{streamId}'}</code>
                <br />
                After connection succeeds, the server immediately sends Metadata packet (Type=0x02)
              </li>
              <li>
                <strong>Receive Metadata</strong>
                <br />
                Parse the Metadata packet's payload (JSON), get audio format:
                <code>sampleRate</code>, <code>channels</code>, <code>codec</code>, etc.
              </li>
              <li>
                <strong>Continuously Receive Audio</strong>
                <br />
                The server continuously sends Audio packets (Type=0x00), each packet's payload is an Opus frame.
                The client needs to:
                <ul>
                  <li>Extract payload (skip 8-byte header)</li>
                  <li>Decode Opus frame to PCM using Opus decoder</li>
                  <li>Play via Web Audio API</li>
                </ul>
              </li>
              <li>
                <strong>Stream End</strong>
                <br />
                Receive Control packet (Type=0x01), <code>type: 3</code> in payload indicates stream end.
                The client should close the WebSocket connection.
              </li>
            </ol>

            <h4>8. Important Notes</h4>
            <ul>
              <li>All multi-byte fields (Magic, Sequence, Length) are <strong>Little-Endian</strong></li>
              <li>Sequence starts from 0, increments by 1 for each packet, can be used to detect packet loss</li>
              <li>Length field represents payload length, excluding the 8-byte header</li>
              <li>Metadata and Control payloads are UTF-8 encoded JSON strings</li>
              <li>Audio packet payload is binary Opus frame, cannot be parsed as string</li>
              <li>A WebSocket message may contain multiple complete packets, need to parse packet by packet according to 8+Length</li>
            </ul>

            <p className="muted-text" style={{ marginTop: '16px' }}>
              The log panel displays all REST requests/responses and WebSocket raw data in real-time. You can refer to these examples to implement your own client.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
